/**
 * /integrations/biometric — M3X.ATTENDANCE F.6 ZK biometric
 * machines registry.
 *
 * Per-LE machine list + status panel. "Sync now" button calls the
 * ZK adapter (mocked v1 per Q1 lock) and refreshes the status.
 * Add Machine modal collects vault-key refs (NOT cleartext per
 * commandment 12).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api, type BiometricMachineDTO } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

export function BiometricMachinesPage() {
  const { t } = useTranslation();
  const legalEntityId = useLegalEntityId();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [syncBusy, setSyncBusy] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["biometric-machines", legalEntityId],
    queryFn: () =>
      api.listBiometricMachines(legalEntityId ?? undefined),
  });

  async function handleSync(machineId: string) {
    setSyncBusy(machineId);
    setSyncResult(null);
    try {
      const r = await api.syncBiometricMachine(machineId);
      setSyncResult(
        r.success
          ? `✓ ${r.punches_received} punches received (status: ${r.machine_status})`
          : `✦ Sync failed: ${r.error_detail ?? r.machine_status}`,
      );
      qc.invalidateQueries({ queryKey: ["biometric-machines"] });
    } finally {
      setSyncBusy(null);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={String((q.error as Error).message)} />;

  const machines = (q.data ?? []) as BiometricMachineDTO[];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "biometric.title",
          "Biometric Machines",
        )}
        subtitle={t(
          "biometric.subtitle",
          "ZK biometric devices per legal entity. Vault-stored credentials per commandment 12. v1 ships with mocked adapter; flips to live when a customer device is connected.",
        )}
        actions={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            disabled={!legalEntityId}
            className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            +{" "}
            {t("biometric.add_machine", "Add machine")}
          </button>
        }
      />

      <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-4 text-xs text-text-secondary">
        ✦{" "}
        {t(
          "biometric.q1_note",
          "v1: mocked ZK adapter with vault credential plumbing. The 'Sync now' button calls the mocked adapter — punches that arrive get processed by Tashghil identically to a live device.",
        )}
      </div>

      {machines.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {t(
            "biometric.empty",
            "No biometric machines configured for this LE. Click 'Add machine' to register one.",
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface-default">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-subtle">
              <tr className="text-text-tertiary">
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.machine_id", "Machine ID")}
                </th>
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.location", "Location")}
                </th>
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.device_type", "Device type")}
                </th>
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.mode", "Mode")}
                </th>
                <th className="px-3 py-2 text-end text-xs uppercase">
                  {t("biometric.sync_interval", "Interval (min)")}
                </th>
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.last_sync", "Last sync")}
                </th>
                <th className="px-3 py-2 text-start text-xs uppercase">
                  {t("biometric.status", "Status")}
                </th>
                <th className="px-3 py-2 text-end text-xs uppercase">
                  {t("biometric.action", "Action")}
                </th>
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => (
                <tr key={m.id} className="border-t border-slate-200">
                  <td className="px-3 py-2 font-mono text-xs">{m.machine_id}</td>
                  <td className="px-3 py-2">{m.location_label || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs uppercase">
                    {m.device_type}
                  </td>
                  <td className="px-3 py-2 text-xs">{m.connection_mode}</td>
                  <td className="px-3 py-2 text-end font-mono">
                    {m.sync_interval_minutes}
                  </td>
                  <td className="px-3 py-2 text-xs text-text-tertiary">
                    {m.last_sync_at?.slice(0, 16) ?? "never"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={m.last_sync_status} />
                    {m.consecutive_failure_count > 0 && (
                      <span className="ms-1 text-[10px] text-rose-700">
                        ({m.consecutive_failure_count} fails)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <button
                      type="button"
                      onClick={() => handleSync(m.id)}
                      disabled={syncBusy !== null}
                      className="text-xs text-brand-blue hover:underline disabled:opacity-50"
                    >
                      {syncBusy === m.id
                        ? t("biometric.syncing", "Syncing…")
                        : t("biometric.sync_now", "✦ Sync now")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {syncResult && (
        <div className="rounded-md border border-slate-200 bg-surface-subtle p-3 text-xs">
          {syncResult}
        </div>
      )}

      {showAdd && legalEntityId && (
        <AddMachineModal
          legalEntityId={legalEntityId}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ["biometric-machines"] });
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-text-tertiary">
        never synced
      </span>
    );
  }
  const cls =
    status === "ok"
      ? "bg-emerald-100 text-emerald-900"
      : status === "offline"
        ? "bg-amber-100 text-amber-900"
        : "bg-rose-100 text-rose-900";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

function AddMachineModal({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [machineId, setMachineId] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [connectionMode, setConnectionMode] = useState("pull");
  const [syncInterval, setSyncInterval] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api.createBiometricMachine({
        legal_entity_id: legalEntityId,
        machine_id: machineId,
        device_type: "zk",
        location_label: locationLabel || null,
        connection_mode: connectionMode,
        sync_interval_minutes: syncInterval,
      });
      onCreated();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-surface-default p-6 shadow-xl">
        <h2 className="mb-3 text-base font-bold">
          {t("biometric.add_title", "Add ZK biometric machine")}
        </h2>
        <div className="space-y-2 text-sm">
          <label className="block">
            <span className="text-xs text-text-secondary">
              {t("biometric.machine_id", "Machine ID")} *
            </span>
            <input
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
              placeholder="Main-Entry-1"
            />
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">
              {t("biometric.location", "Location label")}
            </span>
            <input
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
              placeholder="Main Entrance"
            />
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">
              {t("biometric.mode", "Connection mode")}
            </span>
            <select
              value={connectionMode}
              onChange={(e) => setConnectionMode(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            >
              <option value="pull">PULL — Solvait polls device</option>
              <option value="push">PUSH — device webhooks Solvait</option>
              <option value="both">BOTH</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-text-secondary">
              {t("biometric.sync_interval", "Sync interval (min)")}
            </span>
            <input
              type="number"
              min={1}
              max={60}
              value={syncInterval}
              onChange={(e) => setSyncInterval(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
          <p className="text-[10px] text-text-tertiary">
            {t(
              "biometric.vault_note",
              "IP + password live in the credential vault (commandment 12). Provision separately via /settings/integrations.",
            )}
          </p>
          {err && (
            <p className="rounded bg-rose-50 border border-rose-200 p-2 text-xs text-rose-700">
              {err}
            </p>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          >
            {t("biometric.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!machineId || busy}
            className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy
              ? t("biometric.creating", "Creating…")
              : t("biometric.create", "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BiometricMachinesPage;
